import { parseScene } from "@/utils/parseScene";

// What a user can change about a canvas design, and how far a number has to move before
// it counts. The canvas reports scene_modified for much that is not a change — loading,
// re-applying a scene, re-serialising it with other key order or float noise — so a
// change is judged by what the design looks like, never by the event alone.
//
// Units are the canvas's own: positions and sizes in millimetres, rotations in radians,
// scale as a factor.
const TOLERANCE = { position: 0.01, rotation: 0.001, scale: 0.0001, size: 0.01 };
const AXES = ["x", "y", "z"];
const TEXT_STYLE = ["fontFamily", "fontStyle", "alignHorizontal", "lineSpacing"];
const SIZE = ["width", "height", "depth"];

const differs = (a, b, tolerance) => Math.abs((a ?? 0) - (b ?? 0)) > tolerance;
const vectorDiffers = (a, b, tolerance) =>
  AXES.some((axis) => differs(a?.[axis], b?.[axis], tolerance));

// The photo is the canvas's image entity; everything else the user adds is text.
const subjectOf = (entity) => (entity.type === "text" ? "text" : "photo");

const transformChanges = (before = {}, after = {}) =>
  [
    vectorDiffers(before.position, after.position, TOLERANCE.position) && "moved",
    vectorDiffers(before.scale, after.scale, TOLERANCE.scale) && "resized",
    vectorDiffers(before.rotation, after.rotation, TOLERANCE.rotation) && "rotated",
  ].filter(Boolean);

const entityChanges = (before, after) => {
  const subject = subjectOf(after);
  const changes = transformChanges(before.transform, after.transform);

  if (subject === "photo") {
    // "none" keeps the background, "automatic" cuts it away.
    if (before.mask?.type !== after.mask?.type) changes.push("background");
    if (before.source?.projectAssetId !== after.source?.projectAssetId) changes.push("replaced");
  } else {
    // A text entity's words live in `name`.
    if ((before.name ?? "") !== (after.name ?? "")) changes.push("edited");
    if (TEXT_STYLE.some((key) => before[key] !== after[key])) changes.push("restyled");
  }

  return changes.map((change) => ({ subject, change }));
};

const templateChanged = (before = {}, after = {}) =>
  before.geometryName !== after.geometryName ||
  SIZE.some((side) => differs(before.size?.[side], after.size?.[side], TOLERANCE.size));

// Entities carry a stable id; order in the list means nothing.
const byId = (entities) =>
  new Map(entities.map((entity, index) => [entity.id ?? `${entity.type}#${index}`, entity]));

// Lists what differs between two designs, e.g. [{ subject: "photo", change: "moved" }].
// An empty list means the design is the same, whatever the canvas reported.
export const sceneChanges = (beforeScene, afterScene) => {
  const before = parseScene(beforeScene) || {};
  const after = parseScene(afterScene) || {};
  const beforeEntities = byId(before.entities || []);
  const afterEntities = byId(after.entities || []);
  const changes = [];

  afterEntities.forEach((entity, id) => {
    const previous = beforeEntities.get(id);
    if (previous) changes.push(...entityChanges(previous, entity));
    else changes.push({ subject: subjectOf(entity), change: "added" });
  });
  beforeEntities.forEach((entity, id) => {
    if (!afterEntities.has(id)) changes.push({ subject: subjectOf(entity), change: "removed" });
  });
  if (before.template && after.template && templateChanged(before.template, after.template)) {
    changes.push({ subject: "crystal", change: "reshaped" });
  }

  // Two texts both moved are still one "the text was moved".
  const seen = new Set();
  return changes.filter(({ subject, change }) => {
    const key = `${subject}:${change}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const joinWords = (words) =>
  words.length > 1 ? `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}` : words[0];

const describeSubject = (subject, changes) => {
  const parts = [];
  if (changes.includes("added")) parts.push(`${subject} was added`);
  if (changes.includes("removed")) parts.push(`${subject} was removed`);
  if (changes.includes("background")) parts.push(`the ${subject}'s background was changed`);
  if (changes.includes("reshaped")) parts.push(`the ${subject}'s shape was changed`);
  const verbs = changes.filter(
    (change) => !["added", "removed", "background", "reshaped"].includes(change)
  );
  if (verbs.length) parts.push(`the ${subject} was ${joinWords(verbs)}`);
  return parts;
};

// Puts the changes into one sentence for the user, e.g. "The photo was moved and resized".
export const describeSceneChanges = (changes) => {
  const subjects = [...new Set(changes.map((item) => item.subject))];
  const sentence = subjects
    .flatMap((subject) =>
      describeSubject(
        subject,
        changes.filter((item) => item.subject === subject).map((item) => item.change)
      )
    )
    .join(", ");
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};
